import { Table } from 'console-table-printer';
import mapJson from 'jsonpath-mapper';
import pl from 'pluralize';
import { ansiEscapeCodes, first, strlen } from 'printable-characters';
import { ComplexOptions } from 'console-table-printer/dist/src/models/external-table';
import { Node as DeliveryNode } from 'contensis-delivery-api';
import { Entry, Node } from 'contensis-management-api';
import {
  ContentTypeEntriesToMigrate,
  EntriesResult,
  MigrateStatus,
  NodesResult,
  ProjectMigrateTagGroup,
  ProjectNodesToMigrate,
  TagGroupsResult,
  TagsResult,
  WebhooksResult,
} from '~/services';
import chalk from 'chalk';
import { isGuidRegex } from '~/util/guid';
import { Logger } from '~/util/logger';
import { sortStatuses } from '~/util/array';

const sortByStatus = (row1: any, row2: any) => {
  if (
    sortStatuses[row1.status as MigrateStatus] ===
    sortStatuses[row2.status as MigrateStatus]
  )
    return row1.contentTypeId - row2.contentTypeId;

  return (
    sortStatuses[row1.status as MigrateStatus] -
    sortStatuses[row2.status as MigrateStatus]
  );
};

const formatEntryFieldForTable = (v: any, maxColWidth: number) => {
  if (typeof v === 'string')
    return isGuidRegex.test(v)
      ? v
      : v.replace(/"/g, '').substring(0, maxColWidth || 30);
  if (Array.isArray(v))
    return `${v.length} | ${JSON.stringify(v)
      .replace(/"/g, '')
      .substring(0, 50)}`;
  if (v && typeof v === 'object')
    return JSON.stringify(v)
      .replace(/"/g, '')
      .substring(0, maxColWidth || 50);
  if (v === null) return chalk.grey(v);
  return `${typeof v !== 'undefined' ? v : ''} `;
};

const tableDefaults = {
  style: {
    vertical: '',
    headerTop: {
      left: '',
      mid: '',
      right: '',
      other: '',
    },
    headerBottom: {
      left: '-',
      mid: '-',
      right: '-',
      other: '-',
    },
    tableBottom: {
      left: '-',
      mid: '-',
      right: '-',
      other: '-',
    },
  },
};

const logTableData = (
  table: string,
  endingNewline = true,
  displayLength = 30
) => {
  const consoleWidth = process.stdout.columns;
  console.info(
    consoleWidth
      ? table
          .split('\n')
          .slice(0, consoleWidth ? displayLength : undefined)
          .map((line: string) =>
            consoleWidth && strlen(line) > consoleWidth
              ? first(line, consoleWidth)
              : line
          )
          .join('\n')
      : table.replace(ansiEscapeCodes, '')
  );
  const tableArray = table.split('\n');
  if (consoleWidth && tableArray.length > displayLength)
    console.info(`\n`, `- and ${tableArray.length - displayLength} more...`);
  if (endingNewline) console.info(``);
};

export const logEntitiesTable = ({
  entries,
  nodes,
  projectId,
  fields,
}: {
  entries?: Entry[];
  nodes?: (Node | DeliveryNode)[];
  projectId?: string;
  fields?: string[];
}) => {
  // Expects an array of entries or nodes
  if (!Array.isArray(entries) && !Array.isArray(nodes)) return;

  // Set a 'mode' so we can log correctly and switch out default columns later
  const mode =
    (Array.isArray(entries) && 'entries') ||
    (Array.isArray(nodes) && 'nodes') ||
    'never';

  const data = mode === 'entries' ? entries || [] : nodes || [];

  if (!data.length) {
    console.info(
      `\nNo ${mode} found in ${
        projectId && projectId !== 'null'
          ? `${chalk.yellow(projectId)}`
          : 'payload'
      }`
    );
  } else {
    console.info(
      `\nFound ${chalk.cyan(data.length)} ${mode} in source ${
        projectId && projectId !== 'null'
          ? `${chalk.yellow(projectId)}`
          : 'payload'
      }`
    );
    console.info(`--------------------------------------------`);

    // Column titles are set to be the the first entry's columns
    const dataKeys = Object.keys(data[0]).filter((k) =>
      data.some((e: any) => e[k])
    );

    // Columns are set by inbound 'fields' array
    // or a predefined set of predictable entry fields
    // followed by any remaining 'entryKeys'
    const columnTitles = !!fields
      ? fields
      : mode === 'entries'
        ? [
            'sys.id',
            'sys.contentTypeId',
            dataKeys.includes('entryTitle') ? 'entryTitle' : null,
            dataKeys.includes('entryDescription') ? 'entryDescription' : null,
            ...dataKeys.filter(
              (k) => !['sys', 'entryTitle', 'entryDescription'].includes(k)
            ),
          ].filter((k) => k)
        : [
            'id',
            'parentId',
            'childCount',
            'slug',
            'displayName',
            'path',
            data.some((e: any) => e.entry) ? 'entry.sys.contentTypeId' : null,
            data.some((e: any) => e.entryId) ? 'entryId' : 'entry.sys.id',
            'isCanonical',
            ...dataKeys
              .filter(
                (k) =>
                  ![
                    'id',
                    'parentId',
                    'childCount',
                    'slug',
                    'displayName',
                    'path',
                    'entry',
                    'entryId',
                    'isCanonical',
                  ].includes(k)
              )
              .filter((k) => k),
          ];

    // Columns are set in the table from 'columnTitles'
    // Fields specified with dot notation will be normalised to
    // the last part of the field expression
    // we track all used 'keyNames' so we don't overwrite
    // data with a potential key clash with other entry fields
    const keyNames = [] as string[];
    const p = new Table({
      ...tableDefaults,
      columns: columnTitles
        .map((t) => {
          const keyName = t || ''; // t?.substring(t.lastIndexOf('.') + 1 || 0) || '';
          if (keyNames.indexOf(keyName) === -1) {
            keyNames.push(keyName);
            return {
              name: keyName,
              alignment: 'left',
              // minLen: keyName === 'id' ? 38 : undefined,
            };
          }
          return null;
        })
        .filter((t) => t !== null),
    } as ComplexOptions);

    let maxColWidth = 0;
    if (process.stdout.columns / p.table.columns.length > 30) {
      maxColWidth = Number(process.stdout.columns / p.table.columns.length);
    }
    // For each entry we will loop over 'columnTitles' and
    // create a 'jsonpath-mapper' template to find fields by
    // JSONPath expression and allows us to pull field data
    // from deep within the entry JSON
    for (const e of data) {
      const rowMapping = {} as any;
      for (const field of columnTitles) {
        const keyName = field || ''; // field?.substring(field.lastIndexOf('.') + 1 || 0) || '';
        if (typeof rowMapping[keyName] === 'undefined')
          rowMapping[keyName] = {
            $path: field,
            $return: (v: any) => formatEntryFieldForTable(v, maxColWidth),
          };
      }
      // Run 'mapJSON' function over the generated 'rowMapping' template
      // Add the resulting mapped JSON as a table row
      p.addRow(mapJson(e, rowMapping));
    }

    logTableData(p.render());
  }
};

export const logWebhooksPreviewTable = (
  result: WebhooksResult,
  projectIds: string[]
) => {
  if (!result.webhooksToMigrate) return;
  for (const projectId of projectIds) {
    const { totalCount, ...webhooksToMigrateTotals } = (result
      .webhooksToMigrate[projectId] || {}) as ProjectNodesToMigrate;
    const grandTotal = (
      result.webhooks?.[projectId] || Object.values(result.webhooks || {})[0]
    )?.totalCount;
    const title = `\n${
      totalCount > 0 ? `${chalk.cyan(totalCount)}/${grandTotal}` : 'No'
    } ${pl('webhook', totalCount > 0 ? grandTotal : 0)} to migrate into ${Logger.highlightText(
      projectId
    )}`;

    if (Object.keys(webhooksToMigrateTotals).length)
      logMigrateStatusSummaryTable(webhooksToMigrateTotals, title, totalCount);
    if (totalCount > 0 && totalCount < 200) {
      logWebhookIdsTable(result);
    }
    logErrorsTable(result.errors);
  }
};

export const logTagGroupsPreviewTable = (
  result: TagsResult | TagGroupsResult,
  projectIds: string[],
  logChangesOnly = false,
  logErrors = true
) => {
  if (!result.groupsToMigrate) return;
  for (const projectId of projectIds) {
    const { totalCount, ...groupsToMigrateTotals } = (result.groupsToMigrate[
      projectId
    ] || {}) as ProjectNodesToMigrate;
    const grandTotal =
      ('tags' in result &&
        (result.tags?.[projectId] || Object.values(result.tags || {})[0])
          ?.totalGroups) ||
      (result.groups?.[projectId] || Object.values(result.groups || {})[0])
        ?.totalCount;
    const title = `\n${
      totalCount > 0 ? `${chalk.cyan(totalCount)}/${grandTotal}` : 'No'
    } ${pl('tag group', grandTotal)} to migrate into ${Logger.highlightText(
      projectId
    )}`;

    if (
      (logChangesOnly && totalCount) ||
      (!logChangesOnly && Object.keys(groupsToMigrateTotals).length)
    )
      logMigrateStatusSummaryTable(groupsToMigrateTotals, title, totalCount);
    if (totalCount > 0 && totalCount < 200) {
      logTagGroupIdsTable(result, projectId);
    }
    if (logErrors) logErrorsTable(result.errors);
  }
};

export const logTagsPreviewTable = (
  result: TagsResult,
  projectIds: string[]
) => {
  logTagGroupsPreviewTable(result, projectIds, true, false);

  if (!result.tagsToMigrate) return;
  for (const projectId of projectIds) {
    const { totalCount, ...tagsToMigrateTotals } = (result.tagsToMigrate[
      projectId
    ] || {}) as ProjectNodesToMigrate;
    const grandTotal = (
      result.tags?.[projectId] || Object.values(result.tags || {})[0]
    )?.totalCount;
    const title = `\n${
      totalCount > 0 ? `${chalk.cyan(totalCount)}/${grandTotal}` : 'No'
    } ${pl('tag', grandTotal)} to migrate into ${Logger.highlightText(
      projectId
    )}`;

    if (Object.keys(tagsToMigrateTotals).length)
      logMigrateStatusSummaryTable(tagsToMigrateTotals, title, totalCount);
    if (totalCount > 0 && totalCount < 200) {
      logTagIdsTable(result, projectId);
    }
    logErrorsTable(result.errors);
  }
};

export const logNodesPreviewTable = (
  result: NodesResult,
  projectIds: string[]
) => {
  if (!result.nodesToMigrate) return;
  for (const projectId of projectIds) {
    const { totalCount, ...nodesToMigrateTotals } = (result.nodesToMigrate[
      projectId
    ] || {}) as ProjectNodesToMigrate;
    const grandTotal = (
      result.nodes?.[projectId] || Object.values(result.nodes || {})[0]
    )?.totalCount;
    const title = `\n${
      totalCount > 0 ? `${chalk.cyan(totalCount)}/${grandTotal}` : 'No'
    } ${pl('node', totalCount > 0 ? grandTotal : 0)} to migrate into ${Logger.highlightText(
      projectId
    )}`;

    logMigrateStatusSummaryTable(nodesToMigrateTotals, title, totalCount);
    if (totalCount > 0 && totalCount < 200) {
      logNodeIdsTable(result, projectId);
    }
    logErrorsTable(result.errors);
  }
};

export const logEntriesPreviewTable = (
  result: EntriesResult,
  projectIds: string[]
) => {
  const consoleWidth = process.stdout.columns;
  // Don't render this table for sessions without an interactive console
  if (!consoleWidth) return;

  for (const projectId of projectIds) {
    const { totalCount, ...contentTypes } =
      result.entriesToMigrate[projectId] || {};
    const grandTotal = (
      result.entries?.[projectId] || Object.values(result.entries || {})[0]
    )?.totalCount;

    const title = `\n${
      totalCount > 0 ? `${chalk.cyan(totalCount)}/${grandTotal}` : 'No'
    } ${pl('entry', grandTotal)} to ${'updateField' in result ? `update in` : `migrate into`} ${Logger.highlightText(
      projectId
    )}`;

    logMigrateEntriesSummaryTable(contentTypes, title, totalCount);
    if (totalCount > 0 && totalCount < 2000) {
      logEntryIdsTable(result, projectId);
    }
    logErrorsTable(result.errors);
  }
};

export const logMigrateStatusSummaryTable = (
  migrateTotals: {
    [status: string]: number;
  },
  title: string,
  totalCount: number
) => {
  const p = new Table({
    ...tableDefaults,
    title,
    columns: [
      { name: 'status', alignment: 'left', maxLen: 9 },
      { name: 'total', alignment: 'left', color: 'cyan', maxLen: 7 },
    ],
    colorMap: {
      grey: '\x1b[90m', // define customized color
    },
    sort: (row1, row2) => {
      if (
        sortStatuses[row1.status as MigrateStatus] ===
        sortStatuses[row2.status as MigrateStatus]
      )
        return row1.contentTypeId - row2.contentTypeId;

      return (
        sortStatuses[row1.status as MigrateStatus] -
        sortStatuses[row2.status as MigrateStatus]
      );
    },
  });
  for (const [status, total] of Object.entries(migrateTotals)) {
    const color = ['delete', 'error'].includes(status)
      ? 'red'
      : status === 'ignore'
        ? 'grey'
        : ['create', 'two-pass', 'update'].includes(status)
          ? 'yellow'
          : status === 'publish'
            ? 'cyan'
            : status === 'no change'
              ? 'green'
              : 'reset';

    p.addRow({ status, total }, { color });
  }

  logTableData(p.render(), totalCount === 0);
};
export const logMigrateEntriesSummaryTable = (
  contentTypes: ContentTypeEntriesToMigrate,
  title: string,
  totalCount: number
) => {
  const p = new Table({
    ...tableDefaults,
    title,
    columns: [
      { name: 'contentTypeId', alignment: 'left', maxLen: 20 },
      { name: 'status', alignment: 'left', maxLen: 9 },
      { name: 'total', alignment: 'left', color: 'cyan', maxLen: 7 },
    ],
    colorMap: {
      grey: '\x1b[90m', // define customized color
    },
    sort: (row1, row2) => {
      if (
        sortStatuses[row1.contentTypeId as MigrateStatus] ===
        sortStatuses[row2.contentTypeId as MigrateStatus]
      )
        return row1.status - row2.status;

      return (
        sortStatuses[row1.contentTypeId as MigrateStatus] -
        sortStatuses[row2.contentTypeId as MigrateStatus]
      );
    },
  });
  for (const [contentTypeId, counts] of Object.entries(contentTypes)) {
    for (const [status, total] of Object.entries(counts)) {
      const color = ['delete', 'error'].includes(status)
        ? 'red'
        : status === 'ignore'
          ? 'grey'
          : ['create', 'two-pass', 'update'].includes(status)
            ? 'yellow'
            : status === 'publish'
              ? 'cyan'
              : status === 'no change'
                ? 'green'
                : 'reset';

      p.addRow({ contentTypeId, status, total }, { color });
    }
  }

  if (p.table.rows.length > 0) logTableData(p.render(), totalCount === 0, 1000);
};

export const logEntryIdsTable = (result: EntriesResult, projectId: string) => {
  if (!result?.entriesToMigrate?.entryIds) return;
  const { entryIds } = result.entriesToMigrate;

  const p = new Table({
    ...tableDefaults,
    columns: [
      { name: 'id', alignment: 'left' },
      { name: 'contentTypeId', alignment: 'left', maxLen: 20 },
      { name: 'status', alignment: 'left', maxLen: 8 },
      { name: 'updates', alignment: 'left', maxLen: 5 },
      { name: 'entryTitle', alignment: 'left', maxLen: 30 },
      { name: 'newId', alignment: 'left' },
      { name: 'projectId', alignment: 'left' },
    ],
    sort: sortByStatus,
    filter: (row) => row.status !== 'no change',
  });

  let hasUpdatesColumn = false;
  for (const [contentTypeId, entryIdData] of Object.entries(entryIds)) {
    for (const [entryId, entryData] of Object.entries(entryIdData)) {
      for (const [newEntryId, migrateStatus] of Object.entries<any>(
        entryData[projectId]
      )) {
        const updates =
          typeof migrateStatus.diff === 'string'
            ? `-${
                [...(migrateStatus.diff as string).matchAll(/<<\/->>/g)].length
              },+${
                [...(migrateStatus.diff as string).matchAll(/<<\/\+>>/g)].length
              }`
            : '';
        if (updates) hasUpdatesColumn = true;
        p.addRow({
          id: entryId,
          contentTypeId,
          status: migrateStatus.status,
          updates,
          entryTitle: entryData.entryTitle?.substring(0, 30),
          newId: newEntryId,
          projectId,
        });
      }
    }
  }

  p.table.disabledColumns = !hasUpdatesColumn ? ['updates'] : [];

  logTableData(p.render());
};

export const logNodeIdsTable = (result: NodesResult, projectId: string) => {
  if (!result?.nodesToMigrate?.nodeIds) return;
  const { nodeIds } = result.nodesToMigrate;

  const p = new Table({
    ...tableDefaults,
    columns: [
      { name: 'id', alignment: 'left' },
      { name: 'status', alignment: 'left', maxLen: 8 },
      { name: 'updates', alignment: 'left', maxLen: 5 },
      { name: 'path', alignment: 'left' },
      { name: 'displayName', alignment: 'left', maxLen: 16 },
      { name: 'targetId', alignment: 'left' },
      { name: 'parentId', alignment: 'left' },
    ],
    sort: sortByStatus,
    filter: (row) => row.status !== 'no change',
  });

  let hasUpdatesColumn = false;
  for (const [nodeId, nodeData] of Object.entries(nodeIds)) {
    const updates =
      typeof nodeData.diff === 'string'
        ? `-${[...(nodeData.diff as string).matchAll(/<<\/->>/g)].length},+${
            [...(nodeData.diff as string).matchAll(/<<\/\+>>/g)].length
          }`
        : '';
    if (updates) hasUpdatesColumn = true;
    p.addRow({
      id: nodeId,
      status: nodeData.status,
      updates,
      path: nodeData.path,
      displayName: nodeData.displayName?.substring(0, 30).trim(),
      targetId: nodeData.id,
      parentId: nodeData.parentId,
    });
  }

  p.table.disabledColumns = !hasUpdatesColumn ? ['updates'] : [];

  logTableData(p.render());
};

export const logTagGroupIdsTable = (
  result: TagGroupsResult,
  projectId: string
) => {
  if (!result?.groupsToMigrate?.groupIds) return;
  const { groupIds } = result.groupsToMigrate;

  const p = new Table({
    ...tableDefaults,
    columns: [
      { name: 'id', alignment: 'left' },
      { name: 'status', alignment: 'left', maxLen: 8 },
      { name: 'updates', alignment: 'left', maxLen: 5 },
      { name: 'name', alignment: 'left' },
      { name: 'description', alignment: 'left' },
    ],
    sort: sortByStatus,
    filter: (row) => row.status !== 'no change',
  });

  let hasUpdatesColumn = false;
  for (const [groupId, groupData] of Object.entries(groupIds)) {
    const migrateStatus = groupData[projectId] as
      | ProjectMigrateTagGroup
      | undefined;
    const updates =
      typeof migrateStatus?.diff === 'string'
        ? `-${[...(migrateStatus.diff as string).matchAll(/<<\/->>/g)].length},+${
            [...(migrateStatus.diff as string).matchAll(/<<\/\+>>/g)].length
          }`
        : '';
    if (updates) hasUpdatesColumn = true;
    p.addRow({
      id: groupId,
      status: migrateStatus?.status || '-',
      updates,
      name: groupData.name || '?',
      description: groupData.description,
    });
  }

  p.table.disabledColumns = !hasUpdatesColumn ? ['updates'] : [];

  logTableData(p.render());
};

export const logTagIdsTable = (result: TagsResult, projectId: string) => {
  if (!result?.tagsToMigrate?.tagIds) return;
  const { tagIds } = result.tagsToMigrate;

  const p = new Table({
    ...tableDefaults,
    columns: [
      { name: 'id', alignment: 'left' },
      { name: 'value', alignment: 'left' },
      { name: 'status', alignment: 'left', maxLen: 8 },
      { name: 'updates', alignment: 'left', maxLen: 5 },
      { name: 'groupId', alignment: 'left' },
      { name: 'label', alignment: 'left' },
      { name: 'newId', alignment: 'left' },
      { name: 'projectId', alignment: 'left' },
    ],
    sort: sortByStatus,
    filter: (row) => row.status !== 'no change',
  });

  let hasUpdatesColumn = false;
  for (const [groupId, tagIdData] of Object.entries(tagIds)) {
    for (const [tagId, tagData] of Object.entries(tagIdData)) {
      const migrateStatus = tagData[projectId];
      const updates =
        typeof migrateStatus.diff === 'string'
          ? `-${
              [...(migrateStatus.diff as string).matchAll(/<<\/->>/g)].length
            },+${
              [...(migrateStatus.diff as string).matchAll(/<<\/\+>>/g)].length
            }`
          : '';
      if (updates) hasUpdatesColumn = true;
      p.addRow({
        id: tagId,
        value: tagData.value,
        status: migrateStatus.status,
        updates,
        groupId,
        label: JSON.stringify(tagData.label),
        newId: migrateStatus.id,
        projectId,
      });
    }
  }

  p.table.disabledColumns = !hasUpdatesColumn ? ['updates'] : [];

  logTableData(p.render());
};

export const logWebhookIdsTable = (result: WebhooksResult) => {
  if (!result?.webhooksToMigrate?.webhookIds) return;
  const { webhookIds } = result.webhooksToMigrate;

  const p = new Table({
    ...tableDefaults,
    columns: [
      { name: 'name', alignment: 'left', maxLen: 16 },
      { name: 'enabled', alignment: 'left' },
      { name: 'status', alignment: 'left', maxLen: 8 },
      { name: 'updates', alignment: 'left', maxLen: 5 },
      { name: 'id', alignment: 'left' },
      { name: 'url', alignment: 'left' },
      { name: 'topics', alignment: 'left' },
    ],
    sort: sortByStatus,
    filter: (row) => row.status !== 'no change',
  });

  let hasUpdatesColumn = false;
  for (const [webhookId, webhookData] of Object.entries(webhookIds)) {
    const updates =
      typeof webhookData.diff === 'string'
        ? `-${[...(webhookData.diff as string).matchAll(/<<\/->>/g)].length},+${
            [...(webhookData.diff as string).matchAll(/<<\/\+>>/g)].length
          }`
        : '';
    if (updates) hasUpdatesColumn = true;
    p.addRow({
      enabled: webhookData.enabled,
      id: webhookId,
      status: webhookData.status,
      updates,
      name: webhookData.name.slice(0, 16).trim(),
      url: webhookData.url,
      topics: webhookData.topics,
    });
  }

  p.table.disabledColumns = !hasUpdatesColumn ? ['updates'] : [];

  logTableData(p.render());
};

export const logErrorsTable = (errors: EntriesResult['errors'] = []) => {
  if (!Array.isArray(errors) || !errors.length) return;
  const consoleWidth = process.stdout.columns;
  const p = new Table({
    ...tableDefaults,
    title: `\n${chalk.cyan(errors.length)} ${pl('error', errors.length)}`,
    columns: [
      // { name: 'id', alignment: 'left' },
      {
        name: '',
        alignment: 'left',
        color: 'crimson',
        maxLen: consoleWidth ? consoleWidth - 2 : 80,
      },
    ],
  });

  const destructureErrorMessage = (e: MappedError) => {
    if (typeof e === 'string') {
      return (e as string).substring(0, 300);
    }
    if (Array.isArray(e)) {
      return `${e.length} | ${JSON.stringify(e).substring(0, 300)}`;
    }
    if (e && typeof e === 'object') {
      if ('message' in e) {
        return e.message?.substring(0, 300);
      }
      return JSON.stringify(e).substring(0, 300);
    }
    return `${e} `;
  };

  errors.forEach((e, idx) => {
    p.addRow({ id: idx, ['']: destructureErrorMessage(e) });
  });

  logTableData(p.render());
};

export const logMigrateResultsTable = (
  results:
    | EntriesResult
    | NodesResult
    | TagsResult
    | TagGroupsResult
    | WebhooksResult,
  mode: 'entries' | 'nodes' | 'taggroups' | 'tags' | 'webhooks' = 'entries'
) => {
  let migrateResult:
    | EntriesResult['migrateResult']
    | NodesResult['nodesResult']
    | TagsResult['groupsResult']
    | TagsResult['tagsResult']
    | WebhooksResult['webhooksResult'];

  if (mode === 'entries' && 'migrateResult' in results) {
    migrateResult = results.migrateResult;
  } else if ('nodesResult' in results) {
    migrateResult = results.nodesResult;
  } else if (mode === 'taggroups' && 'groupsResult' in results) {
    migrateResult = results.groupsResult;
  } else if (mode === 'tags' && 'tagsResult' in results) {
    migrateResult = results.tagsResult;
  } else if (mode === 'webhooks' && 'webhooksResult' in results) {
    migrateResult = results.webhooksResult;
  }

  const { errors } = results;
  if (!migrateResult) return;

  const totalCount =
    (migrateResult.created || 0) +
    (migrateResult.updated || 0) +
    (migrateResult.published || 0) +
    (migrateResult.deleted || 0);

  if (totalCount === 0) {
    return;
  }

  const p = new Table({
    ...tableDefaults,
    title: `\n${chalk.cyan(totalCount)} ${pl(mode, totalCount)} migrated`,
    columns: [
      { name: mode, alignment: 'left', maxLen: 8 },
      { name: 'total', alignment: 'right', color: 'cyan', maxLen: 6 },
    ],
  });

  if (Logger.shouldLog('error')) logErrorsTable(errors); // this doesn't work because the data isn't mapped there on commit

  for (const [status, v] of Object.entries(migrateResult)) {
    let total = 0 as number | string;
    if (typeof v === 'string') {
      total = isNaN(Number(v)) ? (v as string).substring(0, 30) : Number(v);
    }
    if (Array.isArray(v)) {
      total = `${v.length} | ${JSON.stringify(v).substring(0, 50)}`;
    } else if (v && typeof v === 'object') {
      total = Object.values(v).flat().length; // JSON.stringify(v).substring(0, 50);
    } else {
      total = isNaN(Number(v)) ? `${v || 0} `.trim() : Number(v);
    }
    const color =
      !isNaN(Number(total)) && Number(total) > 0
        ? ['errors'].includes(status)
          ? 'red'
          : ['created', 'updated'].includes(status)
            ? 'green'
            : status === 'publish'
              ? 'cyan'
              : status === 'deleted'
                ? 'yellow'
                : 'reset'
        : 'reset';
    p.addRow({ [mode]: status, total }, { color });
  }

  logTableData(p.render());
};
